import { advancedFlowSchema_ } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { categories } from "@redotech/redo-flows-common/triggers";
import {
  AutomationChannel,
  AutomationSortKey,
  AutomationStatus,
} from "@redotech/redo-flows-core-schema-common/common";
import { messageAudienceSchema } from "@redotech/redo-marketing-common/performance-report";
import { SortDirection } from "@redotech/redo-views-common/table";
import { unaryRpc } from "@redotech/rpc/definition";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum AutomationsTableChannel {
  Email = AutomationChannel.Email,
  SMS = AutomationChannel.SMS,
  CommerceAgent = "Commerce Agent",
}

export const UserInfo = z.object({
  _id: z.string(),
  name: z.string().nullish(),
  email: z.string().nullish(),
});

export type UserInfo = z.infer<typeof UserInfo>;

const getAdvancedFlowsOutput = z.object({
  advancedFlows: z.array(
    advancedFlowSchema_.extend({
      metrics: z.any().optional(),
      /** What the open rate divides by, since a text can't be opened. */
      emailDeliveries: z.number().optional(),
      isOrigin: z.boolean().optional(),
      canRevert: z.boolean().optional(),
      createdByName: z.string().optional(),
    }),
  ),
  failedToParseFlows: z.array(
    z.object({ _id: z.string(), name: z.string().nullish() }),
  ),
  users: z.map(z.string(), UserInfo).nullish(),
  totalCount: z.number().int(),
});

export const getAdvancedFlows = unaryRpc({
  name: "getAdvancedFlows" as const,
  mock: (generated: z.infer<typeof getAdvancedFlowsOutput>) => ({
    ...generated,
    failedToParseFlows: [],
  }),
  input: z.object({
    getUsers: z.boolean(),
    includeMetrics: z.boolean(),
    includeOriginFlows: z.boolean(),
    searchString: z.string().optional(),
    filters: z
      .object({
        status: z.array(createEnum(AutomationStatus)).optional(),
        channel: z.array(createEnum(AutomationsTableChannel)).optional(),
        category: z.array(createEnum(categories)).optional(),
        schemaType: z.array(createEnum(SchemaType)).optional(),
      })
      .optional(),
    sort: z
      .object({
        key: createEnum(AutomationSortKey),
        direction: createEnum(SortDirection),
      })
      .optional(),
    page: z.optional(z.number().positive().int()),
    pageSize: z.optional(z.number().positive().int()),
    metricsStart: z
      .string()
      .optional()
      .describe("Start date for metrics aggregation (YYYY-MM-DD)"),
    metricsEnd: z
      .string()
      .optional()
      .describe("End date for metrics aggregation (YYYY-MM-DD)"),
    audience: messageAudienceSchema
      .optional()
      .describe(
        "Counts metrics for one audience's messages; omitted counts both",
      ),
  }),
  output: getAdvancedFlowsOutput,
});
